import { toAppError } from '@repo/api-client';
import { useActiveOrganizationId } from '@repo/api-client/react';
import { useClient, useUpdateClient } from '@repo/queries';
import { Loader2 } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { ClientForm } from '@/features/client/ClientForm';
import { clientFormToWire, clientFromWire } from '@/features/client/clientFormSchema';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { AiPill } from '@/features/ai/AiPill';
import { useAiComposer } from '@/features/ai/AiComposerContext';
import { describeAppError } from '@/features/common/describeAppError';
import { NotFoundScreen } from '@/app/public/NotFound';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function ClientEditScreen() {
  const navigate = useNavigate();
  const { canEditOrg } = useActiveRole();
  const params = useParams<{ clientId: string }>();
  const orgId = useActiveOrganizationId();
  const clientId = params.clientId ?? '';
  const { client, loading, error } = useClient(clientId);
  const { updateClient, loading: saving } = useUpdateClient();
  const { toast } = useToast();
  const { open: openAi } = useAiComposer();

  if (!canEditOrg) {
    return <Navigate to={clientId ? `/clients/${clientId}` : '/clients'} replace />;
  }

  if (loading || !client) {
    if (error) {
      return <NotFoundScreen />;
    }
    return (
      <Page>
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title={`Edit ${client.name}`}
        description="Update your client's contact details and preferences."
        breadcrumbs={
          <span>
            <a href="/clients" className="hover:text-foreground">
              Clients
            </a>{' '}
            /{' '}
            <a href={`/clients/${client.id}`} className="hover:text-foreground">
              {client.name}
            </a>{' '}
            / Edit
          </span>
        }
      />

      <AiPill className="mb-5" onClick={() => openAi()} />

      <ClientForm
        loading={saving}
        defaultValues={clientFromWire(client)}
        submitLabel="Save changes"
        onCancel={() => navigate(`/clients/${client.id}`)}
        onSubmit={async (values) => {
          if (!orgId) return;
          try {
            await updateClient({
              organizationId: orgId,
              id: client.id,
              updates: clientFormToWire(values),
            });
            toast({
              variant: 'success',
              title: 'Saved',
              description: 'Client details updated.',
            });
            navigate(`/clients/${client.id}`);
          } catch (err) {
            const copy = describeAppError(toAppError(err));
            toast({
              variant: 'danger',
              title: copy.title,
              description: copy.message,
            });
          }
        }}
      />
    </Page>
  );
}
